import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, ClipboardList, Building2, Users, FileText, LogOut, ExternalLink, ShieldCheck, ShieldAlert, Clock, Bell } from 'lucide-react';

const BrokerLayout = ({ children, profile, remindersCount = 0 }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/broker', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/broker/kyc', icon: ClipboardList, label: 'KYC / Profile' },
    { path: '/broker/projects', icon: Building2, label: 'Projects' },
    { path: '/broker/landing-pages', icon: FileText, label: 'Landing Pages' },
    { path: '/broker/leads', icon: Users, label: 'Leads', badge: remindersCount }
  ];

  const statusBadge = () => {
    const status = profile?.status;
    if (status === 'approved') return { icon: ShieldCheck, text: 'Verified Broker', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
    if (status === 'submitted' || status === 'under_review') return { icon: Clock, text: 'Under Review', color: 'text-amber-700 bg-amber-50 border-amber-200' };
    if (status === 'rejected') return { icon: ShieldAlert, text: 'Rejected', color: 'text-red-700 bg-red-50 border-red-200' };
    return { icon: ShieldAlert, text: 'Complete KYC', color: 'text-slate-600 bg-slate-50 border-slate-200' };
  };

  const badge = statusBadge();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col fixed inset-y-0">
        <Link to="/" className="flex items-center gap-2 p-5 border-b border-slate-200">
          <div className="w-9 h-9 rounded-md bg-[#0F2C59] flex items-center justify-center text-white font-black">P</div>
          <div>
            <div className="font-display font-extrabold text-[#0F2C59]">PropertyByKilo</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Broker Portal</div>
          </div>
        </Link>

        <div className="p-4">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-xs font-semibold ${badge.color}`}>
            <badge.icon size={12} />
            {badge.text}
          </div>
        </div>

        <nav className="flex-1 px-3 pb-4 space-y-1">
          {navItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${isActive ? 'bg-[#0F2C59] text-white' : 'text-slate-700 hover:bg-slate-100'}`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
              >
                <item.icon size={18} />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-[#F97316] text-white" data-testid={`nav-badge-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#0F2C59] text-white flex items-center justify-center text-xs font-bold">
              {user?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
            <button onClick={handleLogout} className="text-slate-500 hover:text-red-600" data-testid="broker-logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64">
        {/* Mobile top bar */}
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <Link to="/broker" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#0F2C59] flex items-center justify-center text-white font-black text-sm">P</div>
            <span className="font-display font-extrabold text-[#0F2C59] text-sm">Broker Portal</span>
          </Link>
          <button onClick={handleLogout} className="text-slate-500"><LogOut size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default BrokerLayout;
